import React, {
    ReactNode,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react'
import { useForm, useWatch, UseFormSetError } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import {
    CheckIcon,
    ClockIcon,
    LinkIcon,
    MagnifyingGlassIcon,
    PencilSquareIcon,
    ShieldCheckIcon,
    SparklesIcon,
    Square2StackIcon,
} from '@heroicons/react/24/outline'

import InputField from 'components/styledFormElements/InputField'
import ArticleSection from './ArticleSection'
import './ArticleEditor.css'
import { ConfirmModal } from 'components/confirmationPopup'
import { LoaderMain } from 'components/loader'
import ArticleContentEditor from 'components/articleEditor/ArticleContentEditor'
import { resolveHtmlMediaSrc, slugify } from 'components/articleEditor/helpers'
import { Article } from 'hooks/article'
import ArticleStatusBadge from './ArticleStatusBadge'
import ArticleSeoSection from './ArticleSeoSection'
import ArticleCategoryField from './ArticleCategoryField'
import ArticleRelatedArticles from './ArticleRelatedArticles'
import { articleSchema, ArticleFormValues } from './validation'

export type SubmitIntent = 'save' | 'publish' | 'submit'

export interface ArticleFormHelpers {
    setError: UseFormSetError<any>
}

interface ArticleFormProps {
    mode: 'create' | 'edit'
    /** Required in edit mode: the article loaded from GET /articles/:id */
    article?: Article | null
    isSubmitting: boolean
    /** Content stays visible but cannot be edited while a review is in progress or after approval. */
    readOnly?: boolean
    canSubmit?: boolean
    canPublish?: boolean
    canReview?: boolean
    /** Rendered above the form (e.g. a version-conflict warning) */
    banner?: ReactNode
    onReview?: (
        decision: 'approved' | 'changes_requested',
        note: string
    ) => Promise<void> | void
    onSubmit: (
        values: ArticleFormValues,
        intent: SubmitIntent,
        helpers: ArticleFormHelpers
    ) => Promise<void> | void
}

const EMPTY_VALUES = {
    title: '',
    slug: '',
    short_description: '',
    author: '',
    content_html: '',
    seoTitle: '',
    metaDescription: '',
    focusKeyword: '',
    searchIntent: '',
    keyTakeaways: ['', '', ''],
    directAnswer: '',
    aiSummary: '',
    faqs: [{ question: '', answer: '' }],
    contentType: 'BlogPosting',
    relatedArticles: '',
    connectedContent: [],
}

const ArticleForm = ({
    mode,
    article,
    isSubmitting,
    readOnly,
    canSubmit,
    canPublish,
    canReview,
    banner,
    onSubmit,
    onReview,
}: ArticleFormProps) => {
    const navigate = useNavigate()
    const [confirmOpen, setConfirmOpen] = useState(false)
    const [reviewNote, setReviewNote] = useState('')
    const [reviewNoteError, setReviewNoteError] = useState('')
    // once the editor types in the slug field we stop deriving it from the title
    const slugTouched = useRef(mode === 'edit')

    const {
        register,
        handleSubmit,
        reset,
        setValue,
        setError,
        control,
        getValues,
        formState: { errors, isDirty },
    } = useForm<any>({
        defaultValues: EMPTY_VALUES,
        resolver: yupResolver(articleSchema) as any,
    })

    const title = useWatch({ control, name: 'title' })
    const shortDescription = useWatch({ control, name: 'short_description' })

    /* ---------- prefill (edit) ---------- */
    useEffect(() => {
        if (mode !== 'edit' || !article) return
        reset({
            title: article.title ?? '',
            slug: article.slug ?? '',
            short_description: article.short_description ?? '',
            author: article.author ?? '',
            content_html: resolveHtmlMediaSrc(article.content_html),
            seoTitle: article.seo_title ?? '',
            metaDescription: article.meta_description ?? '',
            focusKeyword: article.focus_keyword ?? '',
            searchIntent: article.search_intent ?? '',
            keyTakeaways: article.key_takeaways?.length
                ? article.key_takeaways
                : ['', '', ''],
            directAnswer: article.direct_answer ?? '',
            aiSummary: article.geo_summary ?? '',
            faqs: article.faqs?.length
                ? article.faqs
                : [{ question: '', answer: '' }],
            contentType: article.schema_type || 'BlogPosting',
            relatedArticles: (article.related_articles || []).join(', '),
            connectedContent: (article.connected_content || []).map((item) => ({
                contentType: item.content_type,
                contentId: item.content_id,
                name: item.name || '',
            })),
        })
    }, [mode, article, reset])

    /* ---------- slug follows title while creating ---------- */
    useEffect(() => {
        if (mode !== 'create' || slugTouched.current) return
        setValue('slug', slugify(title || ''))
    }, [mode, title, setValue])

    const slugField = register('slug')

    const submitWith = (intent: SubmitIntent) =>
        handleSubmit((values) =>
            onSubmit(values as ArticleFormValues, intent, { setError })
        )

    const handleCancel = useCallback(() => {
        if (isDirty || reviewNote.trim()) setConfirmOpen(true)
        else navigate(-1)
    }, [isDirty, reviewNote, navigate])

    const isCreate = mode === 'create'
    const locked = Boolean(readOnly)

    return (
        <div className="article-workspace w-full text-slate-700 dark:bg-slate-900 dark:text-slate-200">
            <ConfirmModal
                open={confirmOpen}
                title="Are you sure?"
                message={
                    isCreate
                        ? 'This will remove all the data you have entered. This action cannot be undone.'
                        : 'You have unsaved changes. Continue without saving?'
                }
                confirmText="Confirm"
                cancelText="Cancel"
                variant="danger"
                onCancel={() => setConfirmOpen(false)}
                onConfirm={() => {
                    setConfirmOpen(false)
                    navigate(-1)
                }}
            />

            {isSubmitting && (
                <LoaderMain size="md" text="Please wait..." fullscreen />
            )}

            {/* ---------- HEADER ---------- */}
            <header className="article-page-header">
                <div className="min-w-0">
                    <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
                        {isCreate ? 'Create Article' : 'Edit Article'}
                    </h2>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        {isCreate
                            ? 'New articles are saved as drafts. Submit a draft for review when it is ready.'
                            : 'Update the article, then submit it for review. Publishing is available after approval.'}
                    </p>
                </div>
                <div className="article-header-meta">
                    {!isCreate && article && (
                        <dl
                            className="article-metadata"
                            aria-label="Article details"
                        >
                            <div className="article-metadata-item">
                                <dt className="sr-only">Article status</dt>
                                <dd className="article-metadata-status">
                                    <ArticleStatusBadge
                                        status={article.status}
                                    />
                                </dd>
                            </div>
                            <div className="article-metadata-item">
                                <dt className="sr-only">Version</dt>
                                <dd>
                                    <Square2StackIcon
                                        className="h-4 w-4 text-slate-400"
                                        aria-hidden="true"
                                    />
                                    <span>
                                        Version{' '}
                                        <strong className="tabular-nums">
                                            v{article.version}
                                        </strong>
                                    </span>
                                </dd>
                            </div>
                            {article.updated_at && (
                                <div className="article-metadata-item">
                                    <dt className="sr-only">Last updated</dt>
                                    <dd>
                                        <ClockIcon
                                            className="h-4 w-4 shrink-0 text-slate-400"
                                            aria-hidden="true"
                                        />
                                        <time
                                            dateTime={article.updated_at}
                                            title={moment(
                                                article.updated_at
                                            ).format('DD MMM YYYY, h:mm A')}
                                        >
                                            <span className="article-metadata-label">
                                                Last updated
                                            </span>{' '}
                                            {moment(
                                                article.updated_at
                                            ).fromNow()}
                                        </time>
                                    </dd>
                                </div>
                            )}
                        </dl>
                    )}
                    {locked && (
                        <div className="article-mode-badge">
                            <ShieldCheckIcon className="h-4 w-4 shrink-0 text-blue-500" />
                            {canReview
                                ? 'Review mode · Content is read-only'
                                : 'Read-only · Editing is unavailable'}
                        </div>
                    )}
                </div>
            </header>

            {banner}

            <form onSubmit={submitWith('save')} noValidate>
                <div className="article-columns">
                    <fieldset
                        disabled={locked || isSubmitting}
                        className="article-main min-w-0 space-y-5 border-0 p-0"
                    >
                        <legend className="sr-only">Article content</legend>
                        <ArticleSection
                            title="Basic details"
                            description="Title, author, summary, and the article's URL."
                            icon={PencilSquareIcon}
                        >
                            <div className="article-basic-fields">
                                <div className="article-counted-field relative min-w-0">
                                    <span className="article-field-count">
                                        {(title || '').length}/255
                                    </span>
                                    <InputField
                                        id="article-title"
                                        aria-label="Title"
                                        label="Title"
                                        required
                                        helpText="Headline of the article."
                                        placeholder="Enter article title"
                                        type="text"
                                        register={register('title')}
                                        error={
                                            errors?.title?.message as
                                                | string
                                                | ''
                                        }
                                    />
                                </div>

                                <InputField
                                    id="article-author"
                                    aria-label="Author"
                                    label="Author"
                                    required
                                    helpText="Name shown as the article author."
                                    placeholder="Enter author name"
                                    type="text"
                                    register={register('author')}
                                    error={
                                        errors?.author?.message as string | ''
                                    }
                                />
                            </div>

                            <ArticleCategoryField
                                control={control}
                                setValue={setValue}
                                disabled={isSubmitting || locked}
                                error={
                                    errors?.connectedContent?.message as
                                        | string
                                        | undefined
                                }
                            />

                            <div className="mt-4">
                                <label
                                    htmlFor="article-slug"
                                    className="article-field-label"
                                >
                                    Custom URL
                                </label>
                                <div className="article-slug-field">
                                    <span className="article-slug-prefix">
                                        /articles/
                                    </span>
                                    <input
                                        id="article-slug"
                                        type="text"
                                        placeholder="my-article-title"
                                        {...slugField}
                                        onChange={(e) => {
                                            slugTouched.current = true
                                            slugField.onChange(e)
                                        }}
                                        aria-invalid={Boolean(errors.slug)}
                                        aria-describedby="article-slug-help"
                                    />
                                </div>
                                <p
                                    id="article-slug-help"
                                    className={`mt-1.5 text-xs ${
                                        errors.slug
                                            ? 'text-red-500'
                                            : 'text-slate-400'
                                    }`}
                                >
                                    {errors.slug
                                        ? (errors.slug.message as string)
                                        : 'Leave empty to generate from the title. Use lowercase letters, numbers, and hyphens.'}
                                </p>
                            </div>

                            <div className="mt-4 flex flex-col space-y-1">
                                <div className="flex items-center justify-between">
                                    <label
                                        htmlFor="short_description"
                                        className="text-sm font-semibold text-gray-700 dark:text-gray-300"
                                    >
                                        Short description{' '}
                                        <span className="text-red-700">*</span>
                                    </label>
                                    <span className="text-xs tabular-nums text-slate-400">
                                        {(shortDescription || '').length}/500
                                        characters
                                    </span>
                                </div>
                                <textarea
                                    id="short_description"
                                    rows={3}
                                    maxLength={500}
                                    placeholder="Summary shown in listings and search results"
                                    {...register('short_description')}
                                    className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-slate-700"
                                />
                                {errors?.short_description && (
                                    <span className="text-xs text-red-500">
                                        {
                                            errors.short_description
                                                .message as string
                                        }
                                    </span>
                                )}
                            </div>
                        </ArticleSection>

                        <section
                            className="article-panel article-writing-panel"
                            aria-label="Article body"
                        >
                            <ArticleContentEditor
                                name="content_html"
                                control={control}
                                articleId={article?.id}
                                label="Content"
                                required
                                error={
                                    errors?.content_html?.message as
                                        | string
                                        | undefined
                                }
                                disabled={isSubmitting || locked}
                            />
                        </section>

                        <ArticleSection
                            title="Answer engine optimization"
                            description="Help readers and AI answer engines understand the key points."
                            icon={SparklesIcon}
                        >
                            <ArticleSeoSection
                                section="enrichment"
                                articleId={article?.id}
                                control={control}
                                register={register}
                                setValue={setValue}
                                getValues={getValues}
                                errors={errors}
                                disabled={isSubmitting || locked}
                            />
                        </ArticleSection>
                    </fieldset>

                    <aside
                        className="article-sidebar min-w-0 space-y-5"
                        aria-label="Article publishing details"
                    >
                        {canReview && (
                            <section
                                className="article-review-panel"
                                aria-labelledby="editorial-review-heading"
                            >
                                <div className="article-review-heading">
                                    <h3
                                        id="editorial-review-heading"
                                        className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide"
                                    >
                                        <span className="h-2 w-2 rounded-full bg-amber-500" />{' '}
                                        Editorial review note
                                    </h3>
                                    <span className="text-[10px] font-medium">
                                        Required for changes
                                    </span>
                                </div>
                                <div className="p-4">
                                    <label
                                        htmlFor="review-note"
                                        className="mb-2 block text-xs font-medium"
                                    >
                                        Reviewer feedback / revision request
                                    </label>
                                    <textarea
                                        id="review-note"
                                        value={reviewNote}
                                        disabled={isSubmitting}
                                        onChange={(event) => {
                                            setReviewNote(event.target.value)
                                            if (event.target.value.trim())
                                                setReviewNoteError('')
                                        }}
                                        rows={4}
                                        maxLength={2000}
                                        placeholder="Explain what needs updating before publishing…"
                                        aria-invalid={Boolean(reviewNoteError)}
                                        aria-describedby={
                                            reviewNoteError
                                                ? 'review-note-error'
                                                : undefined
                                        }
                                    />
                                    {reviewNoteError && (
                                        <p
                                            id="review-note-error"
                                            role="alert"
                                            className="mt-1 text-xs text-red-600 dark:text-red-400"
                                        >
                                            {reviewNoteError}
                                        </p>
                                    )}
                                    <p className="mt-2 text-xs leading-5 opacity-80">
                                        This feedback will be available to the
                                        author for revision.
                                    </p>
                                </div>
                            </section>
                        )}
                        {article?.review_note && (
                            <div className="rounded border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-900 dark:border-orange-800 dark:bg-orange-950/40 dark:text-orange-100">
                                <p className="font-medium">Review note</p>
                                <p className="mt-1">{article.review_note}</p>
                            </div>
                        )}
                        <fieldset
                            disabled={locked || isSubmitting}
                            className="min-w-0 space-y-5 border-0 p-0"
                        >
                            <legend className="sr-only">Search settings</legend>
                            <ArticleSection
                                title="SEO & search discovery"
                                icon={MagnifyingGlassIcon}
                                description="Control how this article appears in search."
                            >
                                <ArticleSeoSection
                                    section="search"
                                    articleId={article?.id}
                                    control={control}
                                    register={register}
                                    setValue={setValue}
                                    getValues={getValues}
                                    errors={errors}
                                    disabled={isSubmitting || locked}
                                />
                            </ArticleSection>
                            <ArticleSection
                                title="Related articles"
                                icon={LinkIcon}
                                description="Select published articles and set their display order."
                            >
                                <ArticleRelatedArticles
                                    articleId={article?.id}
                                    control={control}
                                    setValue={setValue}
                                    disabled={isSubmitting || locked}
                                    error={
                                        errors?.relatedArticles?.message as
                                            | string
                                            | undefined
                                    }
                                />
                            </ArticleSection>
                        </fieldset>
                    </aside>
                </div>

                {/* ===================== ACTION BAR ===================== */}
                <div className="article-action-bar">
                    <div
                        className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400"
                        role="status"
                    >
                        <span
                            className={`h-2 w-2 shrink-0 rounded-full ${
                                isDirty || reviewNote.trim()
                                    ? 'bg-amber-500'
                                    : 'bg-slate-400'
                            }`}
                        />
                        {isSubmitting
                            ? 'Saving…'
                            : isDirty || reviewNote.trim()
                            ? 'Unsaved changes'
                            : locked
                            ? 'Viewing saved version'
                            : isCreate
                            ? 'New draft · Not saved yet'
                            : 'No unsaved changes'}
                    </div>
                    <div className="flex flex-wrap justify-end gap-2">
                        <button
                            type="button"
                            onClick={handleCancel}
                            disabled={isSubmitting}
                            className="rounded-lg px-4 py-2 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700"
                        >
                            Cancel
                        </button>

                        {!locked && (
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="rounded border border-blue-600 px-6 py-2 text-blue-600 hover:bg-blue-50 disabled:opacity-50 dark:hover:bg-slate-700"
                            >
                                {isCreate ? 'Save draft' : 'Save changes'}
                            </button>
                        )}

                        {canSubmit && (
                            <button
                                type="button"
                                disabled={isSubmitting}
                                onClick={submitWith('submit')}
                                className="rounded bg-blue-600 px-6 py-2 text-white hover:bg-blue-700 disabled:opacity-50"
                            >
                                Submit for review
                            </button>
                        )}

                        {canReview && (
                            <>
                                <button
                                    type="button"
                                    disabled={isSubmitting}
                                    onClick={() => {
                                        if (!reviewNote.trim()) {
                                            setReviewNoteError(
                                                'Add a review note before requesting changes.'
                                            )
                                            document
                                                .getElementById('review-note')
                                                ?.focus()
                                            return
                                        }
                                        onReview?.(
                                            'changes_requested',
                                            reviewNote.trim()
                                        )
                                    }}
                                    className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 text-amber-800 hover:bg-amber-100 disabled:opacity-50 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300 dark:hover:bg-amber-900/40"
                                >
                                    Request changes
                                </button>
                                <button
                                    type="button"
                                    disabled={isSubmitting}
                                    onClick={() =>
                                        onReview?.(
                                            'approved',
                                            reviewNote.trim()
                                        )
                                    }
                                    className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-white hover:bg-emerald-700 disabled:opacity-50"
                                >
                                    <CheckIcon className="h-4 w-4" />
                                    Approve
                                </button>
                            </>
                        )}

                        {canPublish && (
                            <button
                                type="button"
                                disabled={isSubmitting}
                                onClick={submitWith('publish')}
                                className="rounded bg-blue-600 px-6 py-2 text-white hover:bg-blue-700 disabled:opacity-50"
                            >
                                Publish
                            </button>
                        )}
                    </div>
                </div>
            </form>
        </div>
    )
}

export default ArticleForm
